/**
 * Sancta — the SociBaba chatbot persona (like Emmy for Emergent).
 *
 * Floating widget bottom-right on every logged-in page. Streams SSE responses
 * from Claude Sonnet 4.6 (via SociBaba AI). Multilingual, remembers thread,
 * suggests starter prompts.
 *
 * Sancta is the ONLY user-visible use of the word "Sancta". Backend/engine
 * everywhere else is branded "SociBaba AI".
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Loader2, BrainCircuit } from "lucide-react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEntitlements } from "@/hooks/useEntitlements";
import { api } from "@/lib/api";
import { useSanctaLocale } from "@/hooks/useSanctaLocale";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useSpeechSynthesis } from "@/hooks/useSpeechSynthesis";
import { useChatbotConfig, interpolate } from "@/hooks/useChatbotConfig";
import { usePageContext } from "@/hooks/usePageContext";
import { useSanctaThread } from "@/hooks/useSanctaThread";
import { SanctaChatProvider } from "@/contexts/SanctaChatContext";
import { SanctaMarkdown } from "@/components/sancta/SanctaMarkdown";
import { SanctaCheckoutPanel } from "@/components/sancta/SanctaCheckoutPanel";
import { SanctaMemoryDrawer } from "@/components/sancta/SanctaMemoryDrawer";
import { SanctaLauncher } from "@/components/sancta/SanctaLauncher";
import { SanctaHeader } from "@/components/sancta/SanctaHeader";
import { SanctaMessage } from "@/components/sancta/SanctaMessage";
import { SanctaInputBar } from "@/components/sancta/SanctaInputBar";
import { useDragPersist } from "@/hooks/useDragPersist";
import { useProactiveNudge } from "@/hooks/useProactiveNudge";
import { useSanctaSound } from "@/hooks/useSanctaSound";
import { useSanctaIdle } from "@/hooks/useSanctaIdle";
import type { SanctaActivity } from "@/components/sancta/SanctaLauncher";

// Reads the customer's actual message + tone and returns a natural, one-line
// acknowledgement — never a fixed/repeated filler. Buckets are chosen from what
// was specifically said (urgency, frustration, complexity, intent), with light
// variation so it always feels like a human reaction.
function contextualAck(text: string, persona: string): string {
  const t = (text || "").trim();
  const l = t.toLowerCase();
  const pick = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
  const words = t.split(/\s+/).filter(Boolean);

  // Frustration first — de-escalate.
  if (/(frustrat|annoy|angry|ridiculous|useless|worst|terrible|not working|doesn'?t work|still (not|no)|again\?|wtf|ugh|come on|seriously)/.test(l) || /!{2,}/.test(t))
    return pick(["Understood — let me sort that out.", "I hear you — let me fix this.", "Got it, sorting this out right now."]);

  // Urgency.
  if (/(urgent|asap|right now|immediately|hurry|quick(ly)?|fast|need this now|emergency)/.test(l))
    return pick(["On it — right away.", "Right away.", "On it this second."]);

  // Intent-specific.
  if (/https?:\/\//.test(l)) return "Got it — analysing that link now.";
  if (/(where.*(order|package|delivery)|track|shipped|out for delivery|arriv|my order)/.test(l))
    return "Sure — checking your order status.";
  if (/(compare|versus|\bvs\b|difference between)/.test(l)) return "Sure — comparing those now.";
  if (/(cheap|cheaper|budget|under \d|below \d|deal|discount|save money|affordable)/.test(l))
    return "On it — hunting for the best deals.";
  if (/(dupe|alternative|similar to|like this)/.test(l)) return "Let me find close matches.";

  // Complex / multi-part questions.
  const complex = words.length > 14 || (l.includes("?") && /(how|why|explain|which is better|should i|what.s the best)/.test(l));
  if (complex) return pick(["Good question — give me a second.", "Great question — one moment while I dig in.", "Good one — let me think that through."]);

  if (persona === "seller" && /(restock|margin|sales|orders|revenue|grow|perform)/.test(l))
    return "Sure — pulling your numbers.";
  if (persona === "super_admin" && /(gmv|seller|platform|demand|revenue|delay)/.test(l))
    return "Sure — pulling the platform data.";

  // Casual browsing / light search.
  if (/(just (looking|browsing)|any (good |nice )?|show me|recommend|suggest|ideas?)/.test(l))
    return pick(["Sure, let me check that.", "Sure — let me pull a few ideas.", "Happy to — one sec."]);

  // Default product search — echo the query so it feels 'read'.
  if (words.length && words.length <= 6) return pick([`Sure — finding "${t}" for you.`, `On it — looking up "${t}".`]);
  return pick(["Sure, let me check that.", "On it — one moment.", "Got it — looking into that."]);
}

// Persona-specific starter prompts + greeting (Phase 3).
const PERSONA_GREETINGS: Record<string, string> = {
  seller: "Hey! I'm Sancta, your growth advisor 📈 Ask me what to restock, where your margins are strongest, or the one move that'll win you more orders this week.",
  super_admin: "Welcome back. I'm Sancta, your platform intelligence advisor 🧭 Ask me about GMV, top sellers, demand gaps, delivery risk, or your biggest growth lever right now.",
  creator: "Hi! I'm Sancta, your content & growth advisor ✨ Ask me what to post next, how to grow your audience, or how to turn followers into buyers.",
};

const PERSONA_STARTERS: Record<string, Array<{ key: string; text: string }>> = {
  seller: [
    { key: "s-restock", text: "What should I restock this week?" },
    { key: "s-margin", text: "Which products have my best margins?" },
    { key: "s-orders", text: "How do I get more orders?" },
    { key: "s-perf", text: "How is my store performing?" },
  ],
  super_admin: [
    { key: "a-health", text: "Give me a platform health summary" },
    { key: "a-sellers", text: "Who are my top sellers?" },
    { key: "a-demand", text: "Where are the demand gaps?" },
    { key: "a-growth", text: "Where's the biggest growth opportunity?" },
  ],
  creator: [
    { key: "c-next", text: "What should I post next?" },
    { key: "c-grow", text: "How do I grow my audience?" },
    { key: "c-convert", text: "How do I convert followers into buyers?" },
    { key: "c-perf", text: "How is my content performing?" },
  ],
};

const PERSONA_QUICK_REPLIES: Record<string, string[]> = {
  buyer: ["Show cheaper options", "Compare these", "What's trending?", "Track my order"],
  seller: ["Restock ideas", "Boost my margins", "Fulfilment backlog?", "Growth action this week"],
  super_admin: ["Top sellers", "Demand gaps", "Delivery delays", "Revenue opportunities"],
  creator: ["Content ideas", "Best time to post", "Grow audience", "Convert to buyers"],
};

const AUTOSWAP_STORAGE_KEY = "sb_sancta_autoswap_v1";
const AUTOREAD_STORAGE_KEY = "sb_sancta_autoread_v1";

export function SanctaChatbot({ variant = "widget" }: { variant?: "widget" | "page" } = {}) {
  const { user } = useAuth();
  const { persona, isSuperAdmin } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();
  const chatbotCfg = useChatbotConfig();
  const basePageCtx = usePageContext();
  // Phase 1 — persona hint. Backend authoritatively re-resolves (surface wins
  // on shopping pages, role wins on dashboards) but the hint disambiguates
  // seller-vs-creator on workspace surfaces.
  const pageCtx = useMemo(
    () => ({ ...basePageCtx, persona: isSuperAdmin ? "super_admin" : persona }),
    [basePageCtx, persona, isSuperAdmin],
  );
  const { code: langCode, setCode: setLangCode, lang, strings, isRtl } = useSanctaLocale();
  // In `page` mode the chat is always open — no launcher, no close X.
  const [open, setOpen] = useState(variant === "page");
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [starters, setStarters] = useState<Array<{ key: string; text: string }>>([]);
  // Checkout panel — inline commerce surface (Slice 2).
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // Slice 4 — memory drawer opens from header BrainCircuit button.
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [autoSwap, setAutoSwap] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(AUTOSWAP_STORAGE_KEY);
      return saved === null ? true : saved === "1";
    } catch { return true; }
  });
  // Auto-read Sancta's replies aloud as soon as they finish streaming.
  // Defaults to ON for mobile visitors (that's the delightful first impression
  // — the app *speaks* their language) and OFF for desktop, where a talking
  // widget is more likely to interrupt a work session. Users can flip either
  // way; the pick is persisted per browser.
  const [autoRead, setAutoRead] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(AUTOREAD_STORAGE_KEY);
      if (saved !== null) return saved === "1";
    } catch {}
    // First-visit default: honour the platform. Mobile → ON, desktop → OFF.
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 640px)").matches;
  });
  const listRef = useRef<HTMLDivElement | null>(null);

  // Web Speech Synthesis — hear Sancta's replies read aloud in their own
  // language. Zero server cost, native browser voices, per-message id
  // tracking so only one utterance plays at a time.
  const tts = useSpeechSynthesis();

  // Phase 3 — soft results chime + mute, and idle→sleep state machine.
  const sound = useSanctaSound();
  // Display persona mirrors the backend rule: shopping surfaces → buyer,
  // dashboards → role persona. Keeps starters/quick-replies consistent.
  const SHOPPING_SURFACES = ["shop", "ai-cart", "sancta", "public"];
  const rolePersona = isSuperAdmin ? "super_admin" : (persona || "buyer");
  const sanctaPersona = SHOPPING_SURFACES.includes((basePageCtx.surface || "other"))
    ? "buyer"
    : rolePersona;
  const idle = useSanctaIdle(150000, variant === "widget");
  const [resultsBurst, setResultsBurst] = useState(false);
  const prevStreamingRef = useRef(false);
  // Phase 4 — visual search (attach a product photo).
  const [imageSearchOn, setImageSearchOn] = useState(true);
  const [imageBusy, setImageBusy] = useState(false);
  useEffect(() => {
    api.get("/sancta/agent/features")
      .then(r => setImageSearchOn(!!r.data?.image_search_enabled))
      .catch(() => setImageSearchOn(false));
  }, []);

  // Phase 4B — one-time friendly location prompt (buyers) for local relevance.
  const [showLocPrompt, setShowLocPrompt] = useState(false);
  useEffect(() => {
    if (!open || sanctaPersona !== "buyer" || !user) return;
    try {
      if (localStorage.getItem("sb_sancta_loc_v1")) return;
    } catch { return; }
    const tmr = setTimeout(() => setShowLocPrompt(true), 1200);
    return () => clearTimeout(tmr);
  }, [open, sanctaPersona, user]);

  const resolveLocation = useCallback((granted: boolean) => {
    try { localStorage.setItem("sb_sancta_loc_v1", "1"); } catch {}
    setShowLocPrompt(false);
    const post = (lat?: number, lng?: number) =>
      api.post("/sancta/agent/session-context", { lat, lng, consent: granted }).catch(() => {});
    if (granted && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => post(p.coords.latitude, p.coords.longitude),
        () => post(),
        { timeout: 8000, maximumAge: 600000 },
      );
    } else {
      post();
    }
  }, []);

  // Threading + SSE streaming + persistence live in a dedicated hook so
  // this component focuses on layout. `useSanctaThread` owns: messages,
  // input, streaming flag, conversation_id (server-recalled for auth
  // users), detection event, history hydration on open, auto-scroll, and
  // optional TTS auto-read of finished replies. `listRef` is passed in
  // because the messages <div> lives inside the JSX tree below.
  const thread = useSanctaThread({
    user, pageCtx, open, langCode, setLangCode, autoSwap, tts, autoRead,
  });
  // Wire the hook's ref to our list <div> below. useEffect keeps them
  // in sync without reassigning refs at render time.
  useEffect(() => {
    thread.listRef.current = listRef.current;
  });
  const {
    messages, input, setInput, streaming, hydratingHistory, conversationId, detection, limit,
    sendMessage, handleSubmit, handleStarter, pushMessages,
  } = thread;

  // Phase 4 — read an attached product photo, run visual search, and drop the
  // matches into the chat as a product-cards message.
  const handlePickImage = useCallback(async (file: File) => {
    if (imageBusy) return;
    setImageBusy(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result || ""));
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const now = Date.now();
      pushMessages([{ id: `ui_${now}`, role: "user", content: "📷 Find products like this photo", createdAt: now }]);
      const r = await api.post("/sancta/agent/visual-search", { image_base64: dataUrl });
      const items = r.data?.items || [];
      const analysis = r.data?.analysis || {};
      const slugs = items.map((it: any) => it.slug).filter(Boolean);
      const label = analysis.title || (analysis.keywords || []).join(", ") || "your photo";
      const content = slugs.length
        ? `I spotted **${label}** 📸 Here are the closest matches in our shop:\n\n<products>${JSON.stringify({ slugs })}</products>`
        : user
          ? `I looked at your photo (looks like **${label}**) but we don't stock a close match yet. No worries — I'll **email you the moment we get something like this in** 💌`
          : `I looked at your photo (looks like **${label}**) but couldn't find a close match yet. Sign in and I'll alert you when we stock it!`;
      pushMessages([{ id: `ai_${now}`, role: "assistant", content, createdAt: now + 1 }]);
      // No match + signed-in → auto-register a back-in-stock alert.
      if (!slugs.length && user && (analysis.title || (analysis.keywords || []).length)) {
        try { await api.post("/sancta/agent/stock-intent", { query: label, source: "visual" }); } catch {}
      }
      sound.chime();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 402 || status === 429) toast.error(e?.response?.data?.detail || "Out of credits for image search.");
      else if (status === 403) toast.info("Image search is currently turned off.");
      else toast.error(e?.response?.data?.detail || "Couldn't read that image — try a clearer product photo.");
    } finally {
      setImageBusy(false);
    }
  }, [imageBusy, pushMessages, sound]);

  // Listen for `window.dispatchEvent(new CustomEvent("sancta:open", {
  // detail: { prompt } }))` — used by the landing "Try Sancta Live"
  // button and any other CTA that wants to open the widget with a
  // pre-primed question. Zero login required.
  useEffect(() => {
    const handler = (e: Event) => {
      const prompt = (e as CustomEvent).detail?.prompt as string | undefined;
      setOpen(true);
      if (prompt && prompt.trim()) {
        // Delay one tick so the widget mounts + focuses before the
        // stream starts. Otherwise the SSE reply lands in an unmounted
        // list ref and the auto-scroll misses.
        setTimeout(() => sendMessage(prompt.trim()), 150);
      }
    };
    window.addEventListener("sancta:open", handler as EventListener);
    return () => window.removeEventListener("sancta:open", handler as EventListener);
  }, [sendMessage]);

  // Web Speech API voice input — hands-free mode. Auto-fires the message
  // once the user stops speaking. Language follows Sancta's active locale
  // (respects auto-detect swaps from a prior turn).
  // Single entry point for sending — attaches an instant, tone-aware
  // acknowledgement (spoken + shown as a chat line) before the real answer.
  const sendWithAck = useCallback((text: string) => {
    const t = (text || "").trim();
    if (!t) return;
    idle.poke();
    const ack = contextualAck(t, sanctaPersona);
    if (!sound.muted && tts.supported) {
      try { tts.speak(`ack-${Date.now()}`, ack, langCode); } catch {}
    }
    return sendMessage(t, { ack });
  }, [sanctaPersona, sound.muted, tts, langCode, sendMessage, idle]);

  const submitWithAck = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) sendWithAck(input);
  }, [input, sendWithAck]);

  const voice = useVoiceInput({
    language: langCode,
    // Continuous "keep-listening" mode is unreliable on mobile / in-app
    // browsers (Safari restarts choppily) — use single-shot press-to-talk
    // there, and only enable continuous conversation mode on desktop.
    continuousSend: !(typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches),
    onResult: (finalTranscript) => {
      const t = finalTranscript.trim();
      if (t) sendWithAck(t);
    },
  });

  // Phase 3 — chime + burst when a reply finishes; keep the widget awake
  // while anything is happening; wake on new messages / voice / open.
  useEffect(() => {
    const was = prevStreamingRef.current;
    if (was && !streaming) {
      sound.chime();
      setResultsBurst(true);
      const t = setTimeout(() => setResultsBurst(false), 850);
      return () => clearTimeout(t);
    }
    prevStreamingRef.current = streaming;
  }, [streaming]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { idle.poke(); }, [messages.length, open, streaming, voice.state]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Derived launcher activity state for the animated glow ring.
  const activity: SanctaActivity =
    streaming ? "processing"
    : voice.state === "listening" ? "listening"
    : resultsBurst ? "results"
    : (variant === "widget" && idle.asleep && !open) ? "sleeping"
    : "idle";

  // Reflect interim transcript into the input while listening.
  useEffect(() => {
    if (voice.state === "listening" && voice.transcript) {
      setInput(voice.transcript);
    }
  }, [voice.state, voice.transcript]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Surface mic errors as gentle toasts (permission denied etc.)
  useEffect(() => {
    if (voice.error && voice.state === "error") toast.info(voice.error);
  }, [voice.error, voice.state]);

  function toggleVoice() {
    if (!voice.supported) {
      toast.info("Voice isn't supported in this browser. Try Chrome / Safari.");
      return;
    }
    if (streaming) return;  // don't start voice while a reply is still streaming
    if (voice.state === "listening") voice.stop();
    else voice.start();
  }

  useEffect(() => {
    try { localStorage.setItem(AUTOSWAP_STORAGE_KEY, autoSwap ? "1" : "0"); } catch {}
  }, [autoSwap]);

  // Persist auto-read pref so returning shoppers don't get surprised.
  useEffect(() => {
    try { localStorage.setItem(AUTOREAD_STORAGE_KEY, autoRead ? "1" : "0"); } catch {}
  }, [autoRead]);

  // Load starter prompts — key list is language-agnostic, labels come from
  // the active locale so the empty-state pills always render in the user's
  // language even before any real message is sent.
  useEffect(() => {
    // Persona-specific starters (Phase 3) — sellers/admins/creators get
    // advisory prompts; buyers keep the localized shopping starters.
    const personaSet = PERSONA_STARTERS[sanctaPersona];
    if (personaSet) { setStarters(personaSet); return; }
    const localized: Array<{ key: string; text: string }> = [
      { key: "sibascore",     text: strings.starter_sibascore },
      { key: "onboard_brand", text: strings.starter_onboard_brand },
      { key: "aicart",        text: strings.starter_aicart },
      { key: "pricing",       text: strings.starter_pricing },
    ];
    setStarters(localized);
  }, [strings, sanctaPersona]);

  function toggleOpen() {
    setOpen(o => !o);
  }

  const hasMessages = messages.length > 0;
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  const mobileFull = variant === "widget" && isMobile;

  // Widget-only surfaces: launcher rail + draggable dialog. Both remember
  // their per-tab position in sessionStorage so a user's chosen layout
  // sticks for the whole browsing session (matches the product brief).
  const launcherDrag = useDragPersist({
    storageKey: "sb_sancta_launcher_pos_v1",
    width: 56,   // ~= h-14
    height: 56,
    padding: 8,
  });
  const widgetDrag = useDragPersist({
    storageKey: "sb_sancta_widget_pos_v1",
    width: 540,
    height: 640,
    padding: 12,
  });

  // Proactive greeting driven by AI Cart purchase-status tags.
  const { nudge: proactiveNudge, dismiss: dismissNudge } = useProactiveNudge();

  // ─── Header context — stable references so streaming tokens don't
  // ─── force the header to re-render. See SanctaChatContext.tsx for
  // ─── the full rationale.
  const onExpand      = useCallback(() => navigate("/sancta"), [navigate]);
  const onMemoryOpen  = useCallback(() => setMemoryOpen(true), []);
  const onClose       = useCallback(() => setOpen(false), []);
  const taglineText   = useMemo(
    () => interpolate(chatbotCfg.tagline, chatbotCfg),
    [chatbotCfg.tagline, chatbotCfg],
  );
  const headerCtxValue = useMemo(() => ({
    variant,
    isMobile,
    widgetDragProps: variant === "widget" && !isMobile ? widgetDrag.draggableProps : undefined,
    chatbotName: chatbotCfg.name,
    chatbotAccent: chatbotCfg.accent_color,
    tagline: taglineText,
    activeLabel: strings.active_label,
    ttsSpeaking: tts.speaking,
    ttsSupported: tts.supported,
    langCode,
    setLangCode,
    langNativeName: lang.nativeName,
    langPickerOpen,
    setLangPickerOpen,
    detection,
    autoSwap,
    setAutoSwap,
    autoRead,
    setAutoRead,
    voiceReplyOn: strings.voice_reply_on,
    voiceReplyOff: strings.voice_reply_off,
    voiceReplyHint: strings.voice_reply_hint,
    showUserActions: !!user,
    onExpand, onMemoryOpen, onClose,
  }), [
    variant, isMobile, widgetDrag.draggableProps,
    chatbotCfg.name, chatbotCfg.accent_color, taglineText,
    strings.active_label, strings.voice_reply_on, strings.voice_reply_off, strings.voice_reply_hint,
    tts.speaking, tts.supported,
    langCode, setLangCode, lang.nativeName, langPickerOpen,
    detection, autoSwap, autoRead, user,
    onExpand, onMemoryOpen, onClose,
  ]);

  // On the dedicated /sancta full-page route the chat is rendered in
  // variant="page" mode. Skip the floating widget entirely so we don't
  // stack two Sancta surfaces on top of each other.
  if (variant === "widget" && location.pathname.startsWith("/sancta")) {
    return null;
  }

  return (
    <>
      {/* Floating launcher — hidden in `page` mode (the whole browser
          is already the chat surface). Default anchor: right-edge rail
          tab, vertically centered. Users can drag it anywhere and the
          position sticks for the session. */}
      {variant === "widget" && (
        <SanctaLauncher
          open={open}
          drag={launcherDrag}
          onToggle={toggleOpen}
          chatbotName={chatbotCfg.name}
          activity={activity}
        />
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={variant === "page" ? "sancta-page" : "sancta-widget"}
            initial={mobileFull ? { y: "100%" } : { opacity: 0, y: 24, scale: 0.98 }}
            animate={mobileFull ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={mobileFull ? { y: "100%" } : { opacity: 0, y: 12, scale: 0.98 }}
            transition={mobileFull
              ? { type: "spring", stiffness: 320, damping: 34 }
              : { type: "spring", stiffness: 260, damping: 24 }}
            className={
              variant === "page"
                ? `relative bg-white text-neutral-900 dark:bg-slate-900/95 dark:text-white backdrop-blur-md rounded-2xl shadow-2xl shadow-black/30 border border-neutral-200 dark:border-white/10 flex flex-col overflow-hidden h-[calc(100vh-180px)] min-h-[520px]`
                : `fixed bg-white text-neutral-900 flex flex-col overflow-hidden ${
                    mobileFull
                      ? "inset-0 z-[100] h-[100dvh] w-screen"
                      : "z-[94] rounded-2xl shadow-2xl shadow-black/25 border border-neutral-200 w-[540px] h-[640px] max-h-[85vh] max-w-[92vw]"
                  }`
            }
            style={
              mobileFull
                ? { paddingBottom: "env(safe-area-inset-bottom)" }
                : variant === "widget" && !isMobile
                ? (widgetDrag.pos.dragged
                    ? { left: widgetDrag.pos.x, top: widgetDrag.pos.y, right: "auto", bottom: "auto" }
                    // Default: right-anchored, vertically centered. We
                    // compute the numeric top instead of `translateY(-50%)`
                    // because framer-motion drives its own `y` animation
                    // on the motion.div — mixing the two produced a small
                    // vertical jump on first drag (see iter 124).
                    : {
                        right: 72,
                        top: Math.max(24, Math.round((typeof window !== "undefined" ? window.innerHeight : 800) / 2 - 320)),
                      })
                : undefined
            }
            dir={isRtl ? "rtl" : "ltr"}
          >
          <SanctaChatProvider value={headerCtxValue}>
            {/* Header — reads stable state from context, wrapped in
                React.memo so streaming tokens don't re-render it. */}
            <SanctaHeader />

            {/* Body — messages */}
            <div
              ref={listRef}
              data-testid="sancta-messages"
              className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-3 overscroll-contain"
            >
              {hydratingHistory && !hasMessages && (
                <div
                  data-testid="sancta-hydrating-skeleton"
                  className="space-y-3 animate-pulse"
                >
                  {[0, 1, 2].map(i => (
                    <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
                      <div
                        className={`h-10 rounded-2xl ${
                          variant === "page"
                            ? "bg-neutral-200/80 dark:bg-white/10"
                            : "bg-neutral-200/80"
                        } ${i % 2 === 0 ? "w-3/5" : "w-2/5"}`}
                      />
                    </div>
                  ))}
                  <div className={`text-[10px] text-center ${variant === "page" ? "text-neutral-400 dark:text-slate-500" : "text-neutral-400"}`}>
                    Loading conversation…
                  </div>
                </div>
              )}
              {!hasMessages && !hydratingHistory && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white text-[11px] font-semibold shrink-0" style={{ backgroundImage: `linear-gradient(135deg, ${chatbotCfg.accent_color}, #d946ef)` }}>{chatbotCfg.name.charAt(0)}</div>
                    <div className={`text-[11px] pt-1.5 ${variant === "page" ? "text-neutral-500 dark:text-slate-400" : "text-neutral-500"}`}>
                      <span className={`font-medium ${variant === "page" ? "text-neutral-700 dark:text-slate-200" : "text-neutral-700"}`}>{chatbotCfg.name}</span> · {strings.joined_line.replace("Sancta ", "")}
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    data-testid={proactiveNudge ? "sancta-proactive-greeting" : "sancta-default-greeting"}
                    className={`rounded-2xl px-3 py-2.5 text-sm max-w-[85%] ${variant === "page" ? "bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-slate-100" : "bg-neutral-100 text-neutral-800"}`}
                  >
                    {proactiveNudge ? (
                      <SanctaMarkdown text={proactiveNudge.hint} />
                    ) : PERSONA_GREETINGS[sanctaPersona] ? (
                      PERSONA_GREETINGS[sanctaPersona]
                    ) : (
                      interpolate(chatbotCfg.greeting, chatbotCfg).replace("{firstname}", user?.name?.split(" ")[0] || "friend")
                    )}
                  </motion.div>

                  {/* Proactive nudge CTA — click-through action + dismiss. */}
                  {proactiveNudge && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className="flex flex-wrap items-center gap-2"
                      data-testid="sancta-proactive-nudge-actions"
                    >
                      <button
                        data-testid={`sancta-proactive-cta-${proactiveNudge.id}`}
                        onClick={() => {
                          // Deep-link to AI Cart with the correct filter
                          // already selected — matches the click-through
                          // path the on-page banner already uses.
                          const q = proactiveNudge.status ? `?status=${proactiveNudge.status}` : "";
                          navigate(`/ai-cart${q}`);
                          dismissNudge();
                          if (variant === "widget") setOpen(false);
                        }}
                        className="inline-flex items-center gap-1 rounded-full text-[11px] font-semibold px-3 py-1.5 bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white hover:opacity-95 transition-opacity"
                      >
                        {proactiveNudge.cta}
                      </button>
                      <button
                        data-testid="sancta-proactive-dismiss"
                        onClick={dismissNudge}
                        className={`text-[11px] rounded-full px-2 py-1 border transition-colors ${variant === "page" ? "border-neutral-200 dark:border-white/15 text-neutral-500 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-white/10" : "border-neutral-200 text-neutral-500 hover:bg-neutral-100"}`}
                      >
                        Not right now
                      </button>
                    </motion.div>
                  )}

                  {/* Product-context CTA — appears only on a PDP.
                      Sancta is the only support surface, but when the
                      shopper wants a human, we open a direct thread
                      with THAT product's seller (no company support). */}
                  {user && pageCtx.surface === "shop" && pageCtx.product_slug && (
                    <button
                      data-testid="sancta-talk-to-seller"
                      onClick={async () => {
                        try {
                          const r = await api.post("/direct-chat/threads", {
                            product_slug: pageCtx.product_slug,
                            first_message: `Hi! I'm interested in ${pageCtx.product_title || "this product"}. Can you help?`,
                          });
                          const tid = r.data?.thread?.thread_id;
                          if (tid) navigate(`/messages?thread=${tid}`);
                        } catch (e: any) {
                          toast.error(e?.response?.data?.detail || "Could not open a seller chat.");
                        }
                      }}
                      className="w-full flex items-center gap-2 rounded-xl border border-fuchsia-300/70 dark:border-fuchsia-400/40 bg-fuchsia-50 dark:bg-fuchsia-500/10 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-500/20 px-3 py-2 text-left transition-colors"
                    >
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-500 grid place-items-center text-white shrink-0">
                        <MessageCircle className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-fuchsia-900 dark:text-fuchsia-100 truncate">
                          Talk to the seller
                        </div>
                        <div className="text-[10px] text-fuchsia-700 dark:text-fuchsia-300 truncate">
                          Direct chat about {pageCtx.product_title || "this product"}
                        </div>
                      </div>
                    </button>
                  )}

                  <div className={`text-[10px] uppercase tracking-widest pt-2 ${variant === "page" ? "text-neutral-400 dark:text-slate-500" : "text-neutral-400"}`}>{strings.suggestions_header}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {starters.map(s => (
                      <button
                        key={s.key}
                        data-testid={`sancta-starter-${s.key}`}
                        onClick={() => sendWithAck(s.text)}
                        className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                          variant === "page"
                            ? "bg-white border border-neutral-200 hover:border-orange-400 hover:bg-orange-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-200 dark:hover:border-orange-400 dark:hover:bg-white/10"
                            : "bg-white border border-neutral-200 hover:border-orange-400 hover:bg-orange-50"
                        }`}
                      >
                        {s.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <SanctaMessage
                  key={m.id || i}
                  index={i}
                  msg={m}
                  isLast={i === messages.length - 1}
                  streaming={streaming}
                  variant={variant}
                  chatbotName={chatbotCfg.name}
                  chatbotAccent={chatbotCfg.accent_color}
                  conversationId={conversationId}
                  detectionCode={detection?.code}
                  fallbackLang={langCode}
                  thinkingLabel={strings.thinking}
                  tts={tts}
                />
              ))}

              {/* Phase 3 — contextual quick replies under the latest reply. */}
              {hasMessages && !streaming && !limit && messages[messages.length - 1]?.role === "assistant" && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="sancta-quick-replies"
                  className="flex flex-wrap gap-1.5 pl-9"
                >
                  {(PERSONA_QUICK_REPLIES[sanctaPersona] || PERSONA_QUICK_REPLIES.buyer).map((qr) => (
                    <button
                      key={qr}
                      data-testid={`sancta-quick-reply-${qr.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => sendWithAck(qr)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                        variant === "page"
                          ? "bg-white border border-neutral-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-200 dark:hover:border-fuchsia-400 dark:hover:bg-white/10"
                          : "bg-white border border-neutral-200 hover:border-fuchsia-400 hover:bg-fuchsia-50"
                      }`}
                    >
                      {qr}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Input */}
            <AnimatePresence>
              {checkoutOpen && user && (
                <motion.div
                  key="checkout-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-3 pb-2 overflow-hidden"
                >
                  <SanctaCheckoutPanel
                    variant={variant}
                    onClose={() => setCheckoutOpen(false)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {showLocPrompt && (
              <div data-testid="sancta-location-prompt" className="mx-2 mb-1 rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-3 text-xs text-neutral-700">
                <div className="font-semibold text-neutral-900 mb-0.5">Shop what's near you? 📍</div>
                <p className="mb-2 leading-snug">Share your location and I'll prioritise items available locally with faster delivery. We never show your location to anyone.</p>
                <div className="flex gap-2">
                  <button data-testid="loc-allow" onClick={() => resolveLocation(true)} className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-semibold hover:bg-fuchsia-700 transition-colors">Share location</button>
                  <button data-testid="loc-deny" onClick={() => resolveLocation(false)} className="px-3 py-1.5 rounded-full bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-100 transition-colors">Not now</button>
                </div>
              </div>
            )}
            <SanctaInputBar
              variant={variant}
              input={input}
              setInput={setInput}
              streaming={streaming}
              onSubmit={submitWithAck}
              placeholder={strings.input_placeholder}
              sendAriaLabel={strings.send_aria}
              detection={detection}
              autoSwap={autoSwap}
              voice={voice}
              onToggleVoice={toggleVoice}
              showCheckoutBtn={!!user}
              checkoutOpen={checkoutOpen}
              onToggleCheckout={() => setCheckoutOpen(o => !o)}
              muted={sound.muted}
              onToggleMute={sound.toggleMute}
              onPickImage={imageSearchOn ? handlePickImage : undefined}
              imageBusy={imageBusy}
              blocked={!!limit}
              blockedMsg={limit?.mode === "readonly"
                ? "Sancta is briefly read-only — new answers resume soon"
                : (limit?.is_guest
                    ? "Free daily chats used — sign in for more"
                    : "Daily Sancta limit reached — resets at midnight IST")}
            />
          </SanctaChatProvider>
          </motion.div>
        )}
      </AnimatePresence>
      <SanctaMemoryDrawer open={memoryOpen} onClose={() => setMemoryOpen(false)} />
    </>
  );
}

export default SanctaChatbot;
